import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    email: ""
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: credentials.username,
        password: credentials.password
      });
      localStorage.setItem("admin_token", response.data.access_token);
      toast.success("Login successful!");
      navigate("/admin");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid credentials");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/setup`, credentials);
      toast.success("Admin account created! Please login.");
      setIsSetup(false);
    } catch (error) {
      if (error.response?.data?.detail === "Admin already exists. Use login instead.") {
        toast.error("Admin already exists. Please login.");
        setIsSetup(false);
      } else {
        toast.error("Setup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">DC</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-montserrat">
              {isSetup ? "Create Admin Account" : "Admin Login"}
            </h1>
            <p className="text-gray-500 mt-2">
              {isSetup ? "Set up your admin credentials" : "Sign in to manage your bookings"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={isSetup ? handleSetup : handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                placeholder="Enter username"
                className="h-12 mt-1"
                data-testid="admin-username"
              />
            </div>
            {isSetup && (
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  placeholder="Enter email"
                  className="h-12 mt-1"
                  data-testid="admin-email"
                />
              </div>
            )}
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                placeholder="Enter password"
                className="h-12 mt-1"
                data-testid="admin-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="admin-submit"
            >
              {loading ? "Please wait..." : isSetup ? "Create Account" : "Sign In"}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSetup(!isSetup)}
              className="text-primary hover:text-primary/80 text-sm font-medium"
              data-testid="admin-toggle"
            >
              {isSetup ? "Already have an account? Sign in" : "First time? Create admin account"}
            </button>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-6 text-center">
          <a href="/" className="text-gray-500 hover:text-gray-700 text-sm">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
